import React from 'react'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'

interface MetricCardProps {
  title: string
  value: string | number
  subtitle?: string
  trend?: {
    value: number
    isPositive: boolean
  }
  icon?: React.ReactNode
  color?: 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'gray'
}

const colorClasses = {
  blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
  green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
  purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
  orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400',
  red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
  gray: 'bg-gray-50 dark:bg-gray-900/20 text-gray-600 dark:text-gray-400',
}

export function MetricCard({ title, value, subtitle, trend, icon, color = 'blue' }: MetricCardProps) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
      {/* Header with Icon */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {title}
          </p>
        </div>
        {icon && (
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            {icon}
          </div>
        )}
      </div>

      {/* Value */}
      <div className="space-y-1">
        <p className="text-3xl font-bold text-gray-900 dark:text-white">
          {value}
        </p>

        {/* Subtitle or Trend */}
        <div className="flex items-center gap-2">
          {subtitle && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {subtitle}
            </p>
          )}

          {trend && (
            <div className={`flex items-center gap-1 text-sm font-medium ${
              trend.isPositive
                ? 'text-green-600 dark:text-green-400'
                : trend.value === 0
                ? 'text-gray-500 dark:text-gray-400'
                : 'text-red-600 dark:text-red-400'
            }`}>
              {trend.isPositive && <TrendingUp className="w-4 h-4" />}
              {!trend.isPositive && trend.value !== 0 && <TrendingDown className="w-4 h-4" />}
              {trend.value === 0 && <Minus className="w-4 h-4" />}
              <span>{Math.abs(trend.value).toFixed(1)}%</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
